import React, { useState } from 'react';
import { Box, Flex, Heading, Text, useDisclosure, useToast } from '@chakra-ui/react';
// TODO: Migrate other similar components to using @falconeta/capacitor-wifi-connect instead (newer/maintained version)
import { CapacitorWifiConnect } from '@falconeta/capacitor-wifi-connect';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useOnMount } from 'clipsal-cortex-utils/src/hooks/use-on-mount';

import { ArcButton } from '../../../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../../../common/components/SlidingAnimationPageBase';
import { WifiNetwork, WifiNetworkList } from '../../../../../common/components/WifiNetworkList';
import { WiFiPasswordDialog } from '../../../../../common/components/WiFiPasswordDialog';
import { useParamBackUrl } from '../../../../../common/hooks/use-param-back-url';
import { timeout } from '../../../../../utils/common/common-utils';
import { WAIT_FOR_NATIVE_WIFI_MS } from './constants';
import { connectToWifiNetwork, fetchWifiStatus, scanAndFetchWifiNetworks } from './local-wifi-commands';

type LocalSetupStatus = 'READY' | 'SCANNING' | 'CONNECTING';

type State = {
  wifiNetworks: WifiNetwork[];
  selectedWifiNetworkIndex: number | null;
  status: LocalSetupStatus;
};

const INITIAL_STATE: State = {
  wifiNetworks: [],
  selectedWifiNetworkIndex: null,
  status: 'READY',
};

// This component is dependent on the user being connected to the wifi device's local network
// (e.g. initiated via WifiDeviceStart)
export function WifiDeviceLocalSetup() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast({ isClosable: true });
  const backURL = useParamBackUrl('../start?direction=back');
  const [state, setState] = useState(INITIAL_STATE);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { wifiNetworks, selectedWifiNetworkIndex, status } = state;

  async function fetchLocalWifiNetworks() {
    try {
      setState((p) => ({
        ...p,
        status: 'SCANNING',
        selectedWifiNetworkIndex: null,
        wifiNetworks: [],
      }));
      const wifiNetworks = await scanAndFetchWifiNetworks();
      /* istanbul ignore next -- @preserve */
      setState((p) => ({
        ...p,
        status: 'READY',
        wifiNetworks: wifiNetworks.map((network) => ({
          ...network,
          security: [network.security],
          // Flip RSSI from the Ayla device to a positive % signal
          signalStrength: 100 - Math.abs(network.signal),
        })),
      }));
    } catch (e) {
      console.error(e);
      toast.closeAll();
      toast({
        title: t('Set Up Hardware.error fetching wifi'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
      setState((p) => ({
        ...p,
        status: 'READY',
        selectedWifiNetworkIndex: null,
        wifiNetworks: [],
      }));
    }
  }

  useOnMount(fetchLocalWifiNetworks);

  /* istanbul ignore next -- @preserve */
  async function handleConnectDeviceToWifiNetwork(password: string) {
    try {
      if (selectedWifiNetworkIndex === null) {
        const error = new Error('No network selected! Unable to connect device to wifi network if none is selected.');
        Sentry.captureException(error, {
          tags: {
            category: 'feature',
            feature: 'ayla-switches',
            component: 'WifiDeviceLocalSetup',
            method: 'handleConnectDeviceToWifiNetwork',
          },
        });
        throw error;
      }
      setState((p) => ({
        ...p,
        status: 'CONNECTING',
      }));
      const wifiStatus = await fetchWifiStatus();
      await connectToWifiNetwork(wifiNetworks[selectedWifiNetworkIndex].ssid, password);

      // Disconnect from the device's wifi network
      await CapacitorWifiConnect.disconnect();
      // Wait after disconnecting, this helps give the user's phone time to auto reconnect
      await timeout(WAIT_FOR_NATIVE_WIFI_MS);

      navigate(`../register?deviceSerialNumber=${wifiStatus.dsn}`);
    } catch (e) {
      console.error(e);
      toast({
        title: t('Set Up Hardware.error connecting to wifi'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
    setState((p) => ({
      ...p,
      status: 'READY',
    }));
  }

  function handleOnClickWifiNetwork(index: number | null) {
    // Clicking the same network again unselects it
    const newIndex = index === selectedWifiNetworkIndex ? null : index;
    setState((prev) => ({ ...prev, selectedWifiNetworkIndex: newIndex }));
  }

  return (
    <SlidingAnimationPageBase title={t('Common.device setup')} backURL={backURL}>
      <Flex direction="column" h="100%" px={4} pb={10} justify="space-between">
        <Box>
          <Heading size="md">{t('Set Up Hardware.select your wifi')}</Heading>
          <Text mb={2}>{t('Set Up Hardware.select a wifi for device')}</Text>
          <WifiNetworkList
            wifiNetworks={wifiNetworks}
            selectedIndex={selectedWifiNetworkIndex}
            onClick={handleOnClickWifiNetwork}
            isLoading={status === 'SCANNING'}
          />
        </Box>

        <Box textAlign="center">
          <ArcButton
            hidden={!(selectedWifiNetworkIndex === null)}
            data-testid="retry-button"
            onClick={fetchLocalWifiNetworks}
            isLoading={status === 'SCANNING'}
            mt={8}
            w={'80%'}
            arcColor="#3DCD57"
          >
            {t('Set Up Hardware.scan networks')}
          </ArcButton>

          <ArcButton
            hidden={selectedWifiNetworkIndex === null}
            data-testid="continue-button"
            onClick={onOpen}
            isLoading={status === 'CONNECTING'}
            mt={8}
            w={'80%'}
            arcColor="#3DCD57"
          >
            {t('Common.continue')}
          </ArcButton>
        </Box>
      </Flex>

      <WiFiPasswordDialog
        ssid={selectedWifiNetworkIndex !== null ? wifiNetworks[selectedWifiNetworkIndex]?.ssid : ''}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={(password) => {
          handleConnectDeviceToWifiNetwork(password);
        }}
      />
    </SlidingAnimationPageBase>
  );
}
